/**
 * Creates a new object with all properties from the input object except those specified in the keys array.
 *
 * @param obj - The source object to omit properties from
 * @param keys - An array of keys to omit from the object
 * @returns A new object with all properties except those specified in keys
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = omit(obj, ['a', 'b']);
 * // result: { c: 3 }
 */
export const omit = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[],
): Omit<T, K> => {
  if (!keys.length) {
    return obj as Omit<T, K>
  }

  const { [keys[0]]: _omitted, ...rest } = obj
  return keys.length === 1 ? rest : omit(rest as T, keys.slice(1))
}

/**
 * Creates a new object with only the properties specified in the keys array.
 *
 * @param obj - The source object to pick properties from
 * @param keys - An array of keys to include in the new object
 * @returns A new object containing only the properties specified in keys
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const result = pick(obj, ['a', 'b']);
 * // result: { a: 1, b: 2 }
 */

export const pick = <T extends Record<string, unknown>, K extends keyof T>(
  obj: T,
  keys: K[],
): Pick<T, K> => {
  if (!keys.length) {
    return {} as Pick<T, K>
  }

  return keys.reduce(
    (acc, key) => ({ ...acc, [key]: obj[key] }),
    {} as Pick<T, K>,
  )
}

/**
 * Creates a pair of functions to transform values between two sets of data.
 * Only requires a single mapping object, and automatically creates the reverse mapping.
 *
 * @param mapping - An object containing the forward mapping (key to value)
 * @returns An object with two functions: keyToValue and valueToKey
 *
 * @example
 * // Converting between API parameters and UI values
 * const transform = createValuePairing({
 *   firstName: 'first_name',
 *   lastName: 'last_name'
 * })
 * transform.keyToValue('firstName') // returns 'first_name'
 * transform.valueToKey('first_name') // returns 'firstName'
 */
export function createValuePairing<
  TKey extends string | number,
  TValue extends string | number,
>(
  mapping: Record<TKey, TValue>,
): {
  keyToValue: (key: TKey) => TValue
  valueToKey: (value: TValue) => TKey
}

export function createValuePairing<
  TKey extends string | number,
  TValue extends string | number,
>(
  mapping: Record<TKey, TValue>,
  defaultValue: TValue,
): {
  keyToValue: (key: TKey) => TValue
  valueToKey: (value: TValue) => TKey
  getDefaultValue: () => TValue
}

export function createValuePairing<
  TKey extends string | number,
  TValue extends string | number,
>(mapping: Record<TKey, TValue>, defaultValue?: TValue) {
  const reverseMapping = Object.fromEntries(
    Object.entries(mapping).map(([key, value]) => [value, key]),
  ) as Record<TValue, TKey>

  const result = {
    keyToValue: (key: TKey): TValue => mapping[key],
    valueToKey: (value: TValue): TKey => reverseMapping[value],
  }

  if (defaultValue !== undefined) {
    return {
      ...result,
      getDefaultValue: () => defaultValue,
    }
  }

  return result
}
